import { useMemo } from 'react';
import { useStatus } from '../../state/StatusContext';
import { useWorklog } from '../../state/WorklogContext';
import { ALL_EVENTS } from '../../worklog/types';
import { itemPrefix, prefixLabel } from '../../worklog/groups';
import { fmtDuration } from '../../worklog/format';
import { WorklogSection } from '../WorklogSection';
import { CommentsSection } from '../CommentsSection';

interface TaskPageProps {
  taskId: string;
}

export function TaskPage({ taskId }: TaskPageProps) {
  if (taskId.startsWith('item-')) {
    return <ItemDetail id={taskId.slice(5)} />;
  }
  if (taskId.startsWith('group-')) {
    return <GroupDetail prefix={taskId.slice(6)} />;
  }
  return (
    <div className="worklog-empty">unknown task: {taskId}</div>
  );
}

function ItemDetail({ id }: { id: string }) {
  const { snapshot } = useStatus();
  const { state } = useWorklog();
  const item = useMemo(
    () => (snapshot?.items ?? []).find(it => it.id === id) ?? null,
    [snapshot, id],
  );

  // Time for this item within the selected contract (ids repeat across weeks).
  const scopedSeconds = useMemo<number | undefined>(() => {
    const cs = snapshot?.contracts ?? [];
    const target = state.activeContract === ALL_EVENTS
      ? cs.find(c => c.name === 'all_events')
      : cs.find(c => c.name.toLowerCase() === state.activeContract.toLowerCase());
    return target?.item_seconds ? (target.item_seconds[id] ?? 0) : undefined;
  }, [snapshot, state.activeContract, id]);

  if (!item) {
    return <div className="worklog-empty">item not found: {id}</div>;
  }

  const actualS = typeof scopedSeconds === 'number'
    ? scopedSeconds
    : (item.actual_seconds_derived || 0);

  return (
    <>
      <h2>{item.label || item.id}</h2>
      <div className="status-badges">
        <span className="badge contract">{item.id}</span>
        <span className={'badge ' + (item.status || 'open')}>{item.status || 'open'}</span>
        {item.block_reason ? <span className="badge blocked">blocked: {item.block_reason}</span> : null}
      </div>

      <div className="notes-block">
        <p><strong>Time:</strong> {actualS > 0 ? fmtDuration(actualS) : '0'} {item.estimate_minutes ? ' / est ' + fmtDuration(item.estimate_minutes * 60) : ''}</p>
        {typeof item.priority === 'number' && item.priority > 0 ? <p><strong>Priority:</strong> {item.priority}</p> : null}
      </div>

      <CommentsSection itemId={id} />

      <h3>Events tagged {item.id}</h3>
      <WorklogSection scopeId={'item-' + id} scope={{ tags: [id] }} />
    </>
  );
}

function GroupDetail({ prefix }: { prefix: string }) {
  const { snapshot } = useStatus();
  const matchingItems = useMemo(
    () => (snapshot?.items ?? []).filter(it => itemPrefix(it.id) === prefix),
    [snapshot, prefix],
  );
  const tags = useMemo(() => matchingItems.map(it => it.id), [matchingItems]);

  return (
    <>
      <h2>{prefixLabel(prefix)}</h2>
      <p style={{ color: 'var(--text-mute)' }}>{matchingItems.length} items in this group.</p>

      <h3>Events tagged with any of these items</h3>
      <WorklogSection scopeId={'group-' + prefix} scope={{ tags }} />
    </>
  );
}
